/**
 * Esquemas y tipos de **Reportes y alertas** (AF §12) y de la **rentabilidad por sede y
 * actividad** (AF §9.2, RN-CON-17 / RN-CON-18 / RN-CON-18bis).
 *
 * Fuente única entre backend y SPA. Dos reglas gobiernan TODAS las formas de este archivo:
 *
 * 1. **`null` no es cero** (CLAUDE.md). Un total que no se pudo medir, una comparativa sin
 *    base y un objetivo no cargado valen `null` y viajan con su `reason`. La SPA muestra
 *    «sin datos comparables»; jamás un 0 % inventado (AF §12, caso borde interanual).
 * 2. **Cada cifra atribuida lleva su criterio a la vista** (`basis`, RN-CON-18bis): nunca
 *    se presenta una distribución estimada como si fuera un cobro directo.
 */
import { z } from "zod";
import { PAYMENT_METHODS } from "../enums.js";
import { dateISO, dateOnly, objectId, yearMonth } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// Vocabulario
// ─────────────────────────────────────────────────────────────────────────────
const enumOf = (values) => values;
/**
 * Métricas del cache de agregados (`reportAggregates`). Una fila por día × sede × métrica.
 * Es cache **re-derivable** (Arquitectura §11): borrarla y volver a correr el job da lo
 * mismo. No es una entidad de negocio.
 */
export const REPORT_METRICS = enumOf(["revenue", "attendance", "members_active"]);
/**
 * Conceptos de facturación (RN-REP-03: «cuotas, planes 3/6/12, matrículas, productos,
 * extras, eventos, pruebas, convenios»).
 * - `payment_agreement`: cuota de un acuerdo de pago de deuda (`charges.agreement_installment`).
 * - `kiosk`: venta de productos (`payments.saleId`).
 * - `platform_settlement`: cobro de un convenio SportClub/Wellhub (`payer.kind = platform`).
 */
export const REVENUE_CONCEPTS = enumOf([
    "membership_due",
    "plan_installment",
    "enrollment_fee",
    "class_extra",
    "event_fee",
    "trial_class",
    "payment_agreement",
    "kiosk",
    "platform_settlement",
    "other",
]);
/**
 * **El criterio de cada cifra** (RN-CON-18bis). Va pegado al número, no en una nota al pie.
 * - `direct`: cobro directo por esa actividad (extra, pack, `reformer_only`, evento, prueba).
 * - `distributed_by_present`: parte de la cuota del pase libre / del convenio, repartida
 *   por presentes consolidados. Es una atribución, no un cobro.
 * - `direct_cost`: hora liquidada de esa actividad o egreso con `disciplineId`.
 * - `allocated_common`: costo común de la sede repartido por el criterio elegido en
 *   `reports.activityCommonCostsAllocation` (default `none`: no se reparte nada).
 */
export const ATTRIBUTION_BASES = enumOf([
    "direct",
    "distributed_by_present",
    "direct_cost",
    "allocated_common",
]);
/** Rangos del dashboard (RN-REP-02: «cambiar el rango (hoy / semana / mes)»). */
export const DASHBOARD_RANGES = enumOf(["today", "week", "month"]);
/**
 * Criterio de prorrateo de la sede virtual **Central** (RN-CON-17, PV-73).
 * Default del AF: proporcional a la facturación del mes de cada sede.
 */
export const CENTRAL_ALLOCATION_MODES = enumOf(["by_revenue", "equal", "manual"]);
/** Reportes exportables (RN-REP-10: «todo reporte se exporta»). */
export const EXPORTABLE_REPORTS = enumOf([
    "billing",
    "monthly-revenue",
    "active-members",
    "attendance",
    "goals",
    "at-risk",
    "cash-closures",
    "branch-results",
    "activity-profitability",
]);
/** CSV/Excel y PDF (RN-REP-10, rel. 15.2: el cliente mantiene backup físico). */
export const REPORT_EXPORT_FORMATS = enumOf(["csv", "xlsx", "pdf"]);
/** Las dos alertas de negocio de AF §12.4. */
export const BUSINESS_ALERT_KINDS = enumOf(["attendance_drop", "revenue_drop"]);
// ─────────────────────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Dashboard (RN-REP-01/02). Acepta el preset `range` **o** `from`/`to` explícitos: la SPA
 * ya manda `from`/`to` (`web/src/lib/queries/reports.ts`) y el AF habla de presets.
 * Sin nada, es «hoy».
 */
export const dashboardQuery = z.object({
    range: z.enum(DASHBOARD_RANGES).optional(),
    from: dateOnly.optional(),
    to: dateOnly.optional(),
    branchId: objectId.optional(),
});
/** Facturación (RN-REP-03 / RN-REP-14). `granularity` decide si hay actividad distribuida. */
export const billingQuery = z.object({
    from: dateOnly,
    to: dateOnly,
    branchId: objectId.optional(),
    method: z.enum(PAYMENT_METHODS).optional(),
    concept: z.enum(REVENUE_CONCEPTS).optional(),
    disciplineId: objectId.optional(),
    /**
     * RN-REP-14: en la vista **diaria** sólo hay ingresos DIRECTOS por actividad; en la
     * **mensual**, directos + distribuidos por presentes.
     */
    granularity: z.enum(["day", "month"]).default("day"),
});
/** Drill-down hasta el pago puntual y su auditoría (RN-REP-03). */
export const billingPaymentsQuery = billingQuery.extend({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Recaudación mensual con comparativas (RN-REP-07). */
export const monthlyRevenueQuery = z.object({
    month: yearMonth,
    branchId: objectId.optional(),
});
/** Socios activos (RN-REP-04). */
export const activeMembersQuery = z.object({
    at: dateOnly.optional(),
    branchId: objectId.optional(),
});
/** Serie histórica mensual de socios activos (RN-REP-04). */
export const activeMembersSeriesQuery = z.object({
    fromMonth: yearMonth,
    toMonth: yearMonth,
    branchId: objectId.optional(),
});
/** Asistencia (RN-REP-05). */
export const attendanceQuery = z.object({
    from: dateOnly,
    to: dateOnly,
    branchId: objectId.optional(),
    locationId: objectId.optional(),
    disciplineId: objectId.optional(),
});
/** Objetivos por recepcionista (RN-REP-06). */
export const goalsQuery = z.object({
    month: yearMonth.optional(),
    branchId: objectId.optional(),
    userId: objectId.optional(),
});
/** No renovados / en riesgo (RN-REP-08). */
export const atRiskQuery = z.object({
    branchId: objectId.optional(),
    /** Días sin venir. Default del AF: 15 (parámetro `reports.atRiskDays`). */
    days: z.coerce.number().int().min(1).max(365).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Cierres de caja a Administración (RN-REP-09). */
export const cashClosuresQuery = z.object({
    from: dateOnly,
    to: dateOnly,
    branchId: objectId.optional(),
    /** Sólo los que tienen diferencia distinta de cero. */
    withDifference: z.coerce.boolean().optional(),
});
/** Resultado por sede (RN-CON-17/18). */
export const branchResultsQuery = z.object({ month: yearMonth });
/** Rentabilidad por actividad (RN-CON-18bis / RN-REP-14). */
export const activityProfitabilityQuery = z.object({
    month: yearMonth,
    branchId: objectId.optional(),
});
/** Inspección del cache de agregados (Arquitectura §11). */
export const aggregatesQuery = z.object({
    from: dateOnly,
    to: dateOnly,
    branchId: objectId.optional(),
    metric: z.enum(REPORT_METRICS).optional(),
});
/** Re-derivar el cache de un día (o de un rango corto) a mano. */
export const recomputeAggregatesBody = z.object({
    from: dateOnly,
    to: dateOnly.optional(),
    branchId: objectId.optional(),
});
/** Historial de disparos de alertas de negocio (RN-REP-13). */
export const alertHistoryQuery = z.object({
    from: dateISO.optional(),
    to: dateISO.optional(),
    kind: z.enum(BUSINESS_ALERT_KINDS).optional(),
    branchId: objectId.optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * Configuración de las alertas de negocio (RN-REP-11/12/13, PV-81).
 * La de **facturación** la configura sólo el Dueño; la de asistencia, Dueño y Coordinador
 * intersedes (AF §12.4, pantalla «Alertas de negocio»). El recorte lo hace la ruta.
 */
export const alertConfigBody = z.object({
    enabled: z.boolean().optional(),
    attendanceDropPct: z.number().min(1).max(100).optional(),
    revenueDropPct: z.number().min(1).max(100).optional(),
    compareWeeks: z.number().int().min(1).max(26).optional(),
    /** Roles (`owner`, `intersede_coordinator`) o ids de usuario. */
    recipients: z.array(z.string()).min(1).optional(),
    /** Destinatarios de la alerta de FACTURACIÓN: sólo quien ve facturación. */
    revenueRecipients: z.array(z.string()).min(1).optional(),
});
/** Coeficiente de prorrateo de Central (RN-CON-17, PV-73). Sólo Dueño. */
export const centralAllocationBody = z.object({
    mode: z.enum(CENTRAL_ALLOCATION_MODES),
    /** `mode: "manual"` — pesos por sede; se normalizan al 100 %. */
    weights: z.record(objectId, z.number().min(0)).optional(),
});
/** Criterio de reparto de comunes por actividad (RN-CON-18bis, PV-109). Sólo Dueño. */
export const activityCommonCostsBody = z.object({
    allocation: z.enum(["none", "by_present", "by_revenue"]),
});
/** Exporte de cualquier reporte (RN-REP-10). Los filtros son los del reporte pedido. */
export const reportExportQuery = z
    .object({
    report: z.enum(EXPORTABLE_REPORTS),
    format: z.enum(REPORT_EXPORT_FORMATS).default("csv"),
    from: dateOnly.optional(),
    to: dateOnly.optional(),
    month: yearMonth.optional(),
    branchId: objectId.optional(),
    disciplineId: objectId.optional(),
    method: z.enum(PAYMENT_METHODS).optional(),
    concept: z.enum(REVENUE_CONCEPTS).optional(),
    granularity: z.enum(["day", "month"]).optional(),
    days: z.coerce.number().int().min(1).max(365).optional(),
})
    .refine((q) => q.month !== undefined || (q.from !== undefined && q.to !== undefined), {
    message: "Pedí `month` (reportes mensuales) o `from`+`to` (reportes por rango)",
});
// ─────────────────────────────────────────────────────────────────────────────
// Etiquetas (español rioplatense) — las usa el backend y las puede reusar la SPA
// ─────────────────────────────────────────────────────────────────────────────
export const NO_COMPARABLE_DATA = "sin datos comparables";
export const ATTRIBUTION_BASIS_LABELS = {
    direct: "ingreso directo",
    distributed_by_present: "ingreso distribuido por presentes",
    direct_cost: "costo directo liquidado",
    allocated_common: "costo común repartido",
};
export const REVENUE_CONCEPT_LABELS = {
    membership_due: "Cuotas",
    plan_installment: "Planes 3/6/12",
    enrollment_fee: "Matrículas",
    class_extra: "Extras",
    event_fee: "Eventos",
    trial_class: "Pruebas",
    payment_agreement: "Acuerdos de pago",
    kiosk: "Productos",
    platform_settlement: "Convenios",
    other: "Otros",
};
//# sourceMappingURL=reportes.js.map